import dayjs from 'dayjs';
import styled from 'styled-components';
import React, { Dispatch, FC, useEffect, useRef } from 'react';
import { ArrowUpward as ArrowUpwardIcon } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { replace as Replace } from 'redux-first-history';
import { AccessLevel, CompressionFormat, DeliveryLogType, ExportFileFormats } from '@dev/ee-api/ee-api-models';
import { DateDropdown, palette, PrimaryButton, SearchInput, SecondaryButton, SimpleToggle } from '@dev/ee-components';
import { Box, makeStyles } from '@material-ui/core';
import { isEmpty } from 'lodash-es';
import { parse } from 'query-string';
import { useLocation, useRouteMatch } from 'react-router-dom';

import RefreshIcon from '@/images/refreshIcon.svg';
import { ApplicationState } from '@/store';
import { EventLogByChannelNameStart, ExportEvents } from '@/store/reports/actions';
import { EventLogParamsByChannelName } from '@/store/reports/types';
import { ShowDialog } from '@/store/dialog/actions';
import { DateDropdownOptions } from '@/services/dictionaries/DateRangeValues';
import {
    ACTIVITY_TIME_RANGES,
    LONG_DATE_FORMAT,
    SHORT_DATE_FORMAT,
    TIME_RANGE_1_DAY,
    TIME_RANGE_3_DAYS,
    TIME_RANGE_35_DAYS,
    TIME_RANGE_TWO_WEEKS,
    TIME_RANGE_WEEK,
} from '@/services/constants';
import { debounce } from '@/services/tools/debounce';
import { useMobile } from '@/services/hooks/useMobile';
import { addParamToUrl } from '@/services/tools/handleUrlParams';
import { ListFromCampaignParams } from '@/services/ElasticApi';
import { ListFromCampaignStart, ListsLoadStart } from '@/store/lists/actions';
import { getAccountStore } from '@/store/account/actions';
import {
    canSeeExtendedLogs,
    convertMessageCategoryAndStatuses,
    splitSelectedItemsIntoProperState,
} from '@/components/Campaigns/assets/EventFilter/helperFunctions';
import { EventFilter as MobileFilters } from '@/components/Campaigns/assets/EventFilter';
import { getTimeRangeFromQuery } from '@/components/Campaigns/helperFunctions';
import { useDateDropdownValue } from '@/services/hooks/useDateDropdownValue';

import { generateIdBasedOnPath } from '@dev/ee-tools';
import { pathsParams } from '@/services/dictionaries/Paths';
import ExportModal from '../../../../../ExportModal';
import { AddContactsToList } from '../../../../../Contacts/CommonActions/AddToList';
import {
    CreateListButtonProps,
    LastEventOnlyToggleProps,
    RenderSearchInputProps,
} from '../../CampaignReportActivityInterface';
import { useDateDropdownOptions } from '../../hooks/useDateDropdownOptions';
import { useMinMaxDate } from '../../hooks/useMinMaxDate';
import { useRequestParams } from '../../hooks/useRequestParams';

const StyledRefreshIcon = styled.div`
    display: flex;
    align-items: center;
    margin-right: 24px;
    font-size: 28px;
    color: ${palette.gray[6]};
    &:hover {
        cursor: pointer;
    }
`;

const TableHeaderWrapper = styled.div`
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding-right: 48px;
    padding-left: 40px;
`;

const MobileFiltersWrapper = styled.div`
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    flex-direction: column;
`;

const FilterContainer = styled.div`
    width: 100%;
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    & .MuiFormControlLabel-root {
        margin-right: 0;
    }
    & .inputsGroupPanelWrapper {
        padding: 11px;
    }
    & .MuiFormControlLabel-label {
        margin-right: 12px;
    }
`;

const FiltersPopoverWrapper = styled.div`
    width: 100%;
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    & .inputsGroupPanelWrapper {
        padding: 11px;
    }
`;

const useStyles = makeStyles(() => ({
    label: {
        color: palette.gray[7],
        marginRight: '4px',
        marginLeft: 0,
    },
    labelRoot: {
        marginBottom: 0,
    },
}));

const CalendarChange = (requestParams: EventLogParamsByChannelName, startDate: string, endDate: string) => {
    const fromFormated = dayjs(startDate).startOf('day').utc().format(LONG_DATE_FORMAT);
    const toFormated = dayjs(endDate).endOf('day').utc().format(LONG_DATE_FORMAT);

    return { ...requestParams, from: fromFormated, to: toFormated, offset: 0 };
};

const calcDate = (
    requestParams: EventLogParamsByChannelName,
    timeRange: number,
    dispatch: Dispatch<any>
): EventLogParamsByChannelName => {
    const newSearch = addParamToUrl({
        from: dayjs().startOf('day').subtract(timeRange, 'day').format(SHORT_DATE_FORMAT),
        to: dayjs().format(SHORT_DATE_FORMAT),
        page: 1,
    });
    dispatch(Replace({ search: newSearch }));
    return {
        ...requestParams,
        from: dayjs().startOf('day').utc().subtract(timeRange, 'day').format(LONG_DATE_FORMAT),
        to: dayjs().endOf('day').utc().format(LONG_DATE_FORMAT),
        offset: 0,
    };
};

const HandleChange = (
    ev: React.MouseEvent<HTMLElement, MouseEvent>,
    requestParams: EventLogParamsByChannelName,
    itemClicked: string,
    dispatch: Dispatch<any>
) => {
    switch (+itemClicked) {
        case TIME_RANGE_3_DAYS:
            return calcDate(requestParams, TIME_RANGE_3_DAYS, dispatch);
        case TIME_RANGE_WEEK:
            return calcDate(requestParams, TIME_RANGE_WEEK, dispatch);
        case TIME_RANGE_TWO_WEEKS:
            return calcDate(requestParams, TIME_RANGE_TWO_WEEKS, dispatch);
        case TIME_RANGE_35_DAYS:
            return calcDate(requestParams, TIME_RANGE_35_DAYS, dispatch);
        default:
            return calcDate(requestParams, TIME_RANGE_1_DAY, dispatch);
    }
};

const RenderSearchInput: FC<RenderSearchInputProps> = (props: RenderSearchInputProps): JSX.Element => {
    const { requestParams } = props;
    const dispatch = useDispatch();
    const { t } = useTranslation();

    return (
        <SearchInput
            id="searchCampaigns"
            name="searchInput"
            defaultValue={requestParams?.email}
            placeholder={t('searchbyemail')}
            onChange={(event): void => {
                const searchString = event?.target?.value;

                debounce((): void => {
                    const newSearch = addParamToUrl({ search: searchString, page: 1 });
                    dispatch(Replace({ search: newSearch }));
                    dispatch(EventLogByChannelNameStart({ ...requestParams, email: searchString, offset: 0 }));
                }, 500);
            }}
            onDelete={(): void => {
                const newSearch = addParamToUrl({ search: null, page: 1 });
                dispatch(Replace({ search: newSearch }));
                dispatch(EventLogByChannelNameStart({ ...requestParams, email: null, offset: 0 }));
            }}
            type="text"
        />
    );
};

const LastEventOnlyToggle: FC<LastEventOnlyToggleProps> = (props: LastEventOnlyToggleProps): JSX.Element => {
    const { lastEventOnly, requestParams } = props;
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const isMobile = useMobile();
    const classes = useStyles();

    return (
        <Box
            display="inline-flex"
            alignItems="center"
            paddingLeft={!isMobile && '24px'}
            paddingRight={!isMobile && '24px'}
        >
            <SimpleToggle
                id="last-event-only-toggle-button"
                onChange={() => {
                    const newSearch = addParamToUrl({
                        events: null,
                        lastEvent: (!lastEventOnly).toString(),
                        result: null,
                        page: 1,
                    });
                    dispatch(Replace({ search: newSearch }));
                    dispatch(
                        EventLogByChannelNameStart({
                            ...requestParams,
                            statuses: [DeliveryLogType.All],
                            messageCategory: null,
                            lastEvent: !lastEventOnly,
                            offset: 0,
                        })
                    );
                }}
                checked={lastEventOnly}
                value="lasteventonly"
                label={t('lasteventonly')}
                labelPlacement="start"
                classes={{ label: classes.label, labelRoot: classes.labelRoot }}
            />
        </Box>
    );
};

const CreateListButton: FC<CreateListButtonProps> = (props: CreateListButtonProps) => {
    const { lastEventOnly, requestParams } = props;
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const isMobile = useMobile();
    const selectedList = useRef({ list: '', newList: false });

    const route = useRouteMatch<{ type: string }>();

    const routeForIds =
        route?.path?.replace?.(pathsParams.name, '')?.replace(pathsParams.type, route.params?.type) ?? '';

    const onClickCreateListConfirm = (): void => {
        const params: ListFromCampaignParams = {
            campaignName: requestParams.channelName.trim(),
            listName: selectedList.current.list,
            statuses: requestParams.statuses as [],
            messageCategory: requestParams.messageCategory,
            from: requestParams.from,
            to: requestParams.to,
            email: requestParams.email,
            newList: selectedList.current.newList,
            lastEvent: lastEventOnly,
        };

        dispatch(ListFromCampaignStart(params));
    };

    const openCreateListModal = (): void => {
        dispatch(
            ShowDialog({
                title: t('addcontactsctolist'),
                body: (
                    <AddContactsToList
                        moveContacts={false}
                        onChange={(ev, selectedItems) => {
                            selectedList.current = { list: selectedItems.trim(), newList: ev };
                        }}
                    />
                ),
                confirmLabel: t('save'),
                cancelLabel: t('cancel'),
                onClickConfirm: onClickCreateListConfirm,
                isHiddenManually: true,
            })
        );
    };

    return (
        <SecondaryButton
            id={generateIdBasedOnPath('create-list-button', routeForIds)}
            onClick={openCreateListModal}
            size="medium"
            text={t('createlist')}
            variant="text"
            style={{ marginLeft: !isMobile && '32px', marginRight: !isMobile && '16px' }}
        />
    );
};

export interface ExportButtonProps {
    channelName: string;
    search: string;
    lastEventOnly: boolean;
}
const ExportButton: FC<ExportButtonProps> = (props: ExportButtonProps) => {
    const { lastEventOnly, channelName, search } = props;
    const dispatch = useDispatch();
    const { t } = useTranslation();
    let fileNameActivityRef;
    const exportCompressionFormat = useRef(CompressionFormat.Zip);
    const exportFileFormat = useRef(ExportFileFormats.Csv);

    const { eventLogByChannelName, eventLogParamsByChannelName } = useSelector(
        (state: ApplicationState) => state.reports
    );

    const onClickExportConfirm = (): void => {
        dispatch(
            ExportEvents({
                fileName: fileNameActivityRef?.value || 'export',
                fileFormat: exportFileFormat.current,
                compressionFormat: exportCompressionFormat.current,
                channelName,
                statuses: eventLogParamsByChannelName?.statuses,
                messageCategory: eventLogParamsByChannelName?.messageCategory,
                from: eventLogParamsByChannelName.from,
                to: eventLogParamsByChannelName.to,
                lastEvent: lastEventOnly,
                ...(search ? { email: search } : {}),
            })
        );
    };

    const openExportEventsModal = (): void => {
        dispatch(
            ShowDialog({
                title: t('export'),
                body: (
                    <ExportModal
                        onCompressionChange={(enabled: boolean): void => {
                            exportCompressionFormat.current = enabled ? CompressionFormat.Zip : CompressionFormat.None;
                        }}
                        onFileFormatChange={(data: { value: ExportFileFormats; label: string }): void => {
                            exportFileFormat.current = data.value;
                        }}
                        inputRef={(element) => {
                            fileNameActivityRef = element;
                        }}
                    />
                ),
                confirmLabel: t('export'),
                cancelLabel: t('cancel'),
                onClickConfirm: onClickExportConfirm,
            })
        );
    };

    return (
        <PrimaryButton
            id="exportlinks"
            onClick={() => {
                openExportEventsModal();
            }}
            disabled={isEmpty(eventLogByChannelName)}
            size="medium"
            text={t('export')}
            variant="text"
            startIcon={<ArrowUpwardIcon />}
        />
    );
};

const FiltersComponent = (props: { search: string; lastEventOnly: boolean; channelName: string }): JSX.Element => {
    const { search, lastEventOnly, channelName } = props;
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const isMobile = useMobile();
    const { GeneralInfo } = useSelector(getAccountStore);
    const accountAL = GeneralInfo.AccountAccessLevel;
    const location = useLocation();
    const { from, to } = parse(location.search);

    const lists = useSelector((state: ApplicationState) => state.lists);
    const requestParams = useRequestParams(channelName, lastEventOnly);
    const value = getTimeRangeFromQuery(requestParams.to, requestParams.from);
    const timeRangeRef = useRef(value);

    useEffect(() => {
        if (!lists?.collection) {
            dispatch(ListsLoadStart({ modifyQueryString: false }));
        }
    }, []);

    const dateFilterData = useDateDropdownOptions();
    const [minDate, maxDate] = useMinMaxDate();
    const defaultDropdownValue = canSeeExtendedLogs(GeneralInfo.AccountAccessLevel)
        ? DateDropdownOptions.Last35days
        : GeneralInfo.KeepLogsForDays.toString();
    const dateDropdownValue = useDateDropdownValue(true, defaultDropdownValue, ACTIVITY_TIME_RANGES);

    const mobileFiltersApply = (selected, selectedDate, startDate, endDate): void => {
        const [events, selectedResult] = splitSelectedItemsIntoProperState(selected);
        const [convertedStatuses, convertedResults] = convertMessageCategoryAndStatuses(events, selectedResult);
        let newRequestParams: EventLogParamsByChannelName;
        let newDateSearch;

        switch (selectedDate) {
            case DateDropdownOptions.Calendar:
                newDateSearch = {
                    from: dayjs(startDate).format(SHORT_DATE_FORMAT),
                    to: dayjs(endDate).format(SHORT_DATE_FORMAT),
                };
                newRequestParams = CalendarChange(requestParams, startDate, endDate);
                break;
            default:
                newRequestParams = HandleChange(null, requestParams, selectedDate, dispatch);
                break;
        }
        newRequestParams.statuses = isEmpty(convertedStatuses) ? [DeliveryLogType.All] : convertedStatuses;
        newRequestParams.messageCategory = isEmpty(selectedResult) ? null : convertedResults;
        const newSearch = addParamToUrl({
            events: !isEmpty(convertedStatuses) && convertedStatuses,
            result: !isEmpty(selectedResult) && convertedResults,
            page: 1,
            ...newDateSearch,
        });
        dispatch(Replace({ search: newSearch }));
        dispatch(EventLogByChannelNameStart(newRequestParams));
    };

    if (isMobile) {
        return (
            <MobileFiltersWrapper>
                <FiltersPopoverWrapper>
                    <RenderSearchInput requestParams={requestParams} />
                    <MobileFilters onHeadersEventFilterClick={mobileFiltersApply} timeRangeValue={value} />
                </FiltersPopoverWrapper>
                <FilterContainer>
                    {accountAL.includes(AccessLevel.ModifyContacts) && (
                        <CreateListButton requestParams={requestParams} lastEventOnly={lastEventOnly} />
                    )}
                    <LastEventOnlyToggle lastEventOnly={lastEventOnly} requestParams={requestParams} />
                </FilterContainer>
            </MobileFiltersWrapper>
        );
    }

    return (
        <TableHeaderWrapper>
            <Box display="inline-flex" justifyContent="flex-start" width="40%">
                <DateDropdown
                    options={dateFilterData}
                    defaultPlaceholder={
                        canSeeExtendedLogs(GeneralInfo.AccountAccessLevel) ? t('last35days') : t('last3days')
                    }
                    value={dateDropdownValue}
                    type="input"
                    returnRawData
                    calendarOnChange={(startDate?: string, endDate?: string) => {
                        const newSearch = addParamToUrl({
                            from: dayjs(startDate).format(SHORT_DATE_FORMAT),
                            to: dayjs(endDate).format(SHORT_DATE_FORMAT),
                            page: 1,
                        });
                        dispatch(Replace({ search: newSearch }));
                        dispatch(EventLogByChannelNameStart(CalendarChange(requestParams, startDate, endDate)));
                    }}
                    onItemClick={(ev, value?: Record<string, string | number>) => {
                        timeRangeRef.current = value.value as number;
                        dispatch(
                            EventLogByChannelNameStart(HandleChange(ev, requestParams, value.value as string, dispatch))
                        );
                    }}
                    buttonVariant="tertiary"
                    minDate={minDate}
                    maxDate={maxDate}
                    startDate={from?.toString()}
                    endDate={to?.toString()}
                />
                <LastEventOnlyToggle lastEventOnly={lastEventOnly} requestParams={requestParams} />
            </Box>
            <Box display="inline-flex" justifyContent="flex-end" width="60%">
                <StyledRefreshIcon
                    onClick={() => {
                        dispatch(EventLogByChannelNameStart(requestParams));
                    }}
                >
                    <RefreshIcon />
                </StyledRefreshIcon>
                <Box paddingRight="16px">
                    <RenderSearchInput requestParams={requestParams} />
                </Box>
                {accountAL.includes(AccessLevel.ModifyContacts) && (
                    <CreateListButton requestParams={requestParams} lastEventOnly={lastEventOnly} />
                )}
                {accountAL.includes(AccessLevel.Export) && (
                    <ExportButton lastEventOnly={lastEventOnly} search={search} channelName={channelName} />
                )}
            </Box>
        </TableHeaderWrapper>
    );
};

export default FiltersComponent;
